import {
  WSButton,
  WSCard,
  WSFlexBox,
  WSList,
  WSPill,
  WSPillTheme,
  WSText,
  toWSDateString
} from "@wingspanhq/fe-component-library";
import React from "react";
import { useMutationDownloadEmployeeTaxFormPdf } from "../../../query/employeeTaxForms/mutations/useMutationDownloadEmployeeTaxFormPdf";
import { ErrorAlert } from "../../../v3/components/ErrorAlert";
import { routeHelpersEmployeeTaxFormsForm } from "../paths";
import { IFormChecklistItem } from "../types";
import { asFormMetadata } from "../../DynamicForm/utils/formMetadata";

type Props = {
  item: IFormChecklistItem;
  /** When set, the renderer returns here after submit (requirement page). */
  backPath?: string;
};

const statusToPill = (
  status: IFormChecklistItem["status"]
): { theme: WSPillTheme; text: string; icon: boolean } => {
  switch (status) {
    case "Completed":
      return { theme: "success", text: "Completed", icon: true };
    case "Rejected":
      return { theme: "warning", text: "Needs action", icon: true };
    default:
      return { theme: "neutral", text: "Not started", icon: false };
  }
};

export const ChecklistItem: React.FC<Props> = ({ item, backPath }) => {
  const goToForm = routeHelpersEmployeeTaxFormsForm.useGoTo();
  const [downloadFormPdf, downloadFormPdfMeta] =
    useMutationDownloadEmployeeTaxFormPdf();
  const pill = statusToPill(item.status);
  const ctaLabel =
    item.status === "Completed"
      ? "Review"
      : item.status === "Rejected"
        ? "Resubmit"
        : "Start";
  const jurisdiction = asFormMetadata(item.metadata)?.jurisdiction;

  return (
    <WSCard>
      <WSFlexBox.CenterY justify="space-between" wrap="wrap" gap="M">
        <WSList gap="XS">
          <WSFlexBox.CenterY gap="S">
            <WSText.Paragraph weight="medium">{item.title}</WSText.Paragraph>
            <WSPill theme={pill.theme} text={pill.text} icon={pill.icon} />
          </WSFlexBox.CenterY>
          {jurisdiction ? (
            <WSText.ParagraphSm color="gray500">
              {jurisdiction.country}
              {jurisdiction.state ? ` · ${jurisdiction.state}` : ""}
            </WSText.ParagraphSm>
          ) : null}
          {item.status === "Completed" && item.completedAt ? (
            <WSText.ParagraphSm color="gray500">
              Submitted on {toWSDateString(item.completedAt)}
            </WSText.ParagraphSm>
          ) : null}
          <ErrorAlert
            context="EmployeeTaxFormPdf"
            error={downloadFormPdfMeta.error}
          />
        </WSList>
        <WSFlexBox.CenterY gap="M">
          {item.status === "Completed" ? (
            // Icon-only download per the Info Architecture Figma (Forms card):
            // a bare 16px download glyph on submitted rows, no text label.
            <WSButton
              kind="Link"
              size="S"
              icon="download"
              onlyIcon
              name="downloadFormPdf"
              loading={downloadFormPdfMeta.isLoading}
              onClick={() =>
                downloadFormPdf({
                  formInstanceId: item.id,
                  filename: `${item.title}.pdf`
                })
              }
            />
          ) : null}
          <WSButton
            kind="Secondary"
            size="S"
            rightIcon="chevron-right"
            onClick={() =>
              goToForm(
                { formInstanceId: item.id },
                { state: backPath ? { backPath } : undefined }
              )
            }
          >
            {ctaLabel}
          </WSButton>
        </WSFlexBox.CenterY>
      </WSFlexBox.CenterY>
    </WSCard>
  );
};
